import React, { useEffect, useState } from "react";
import {
  X,
  CreditCard,
  History,
  CheckCircle,
  Clock,
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  Package,
  IndianRupee,
  AlertCircle,
  Shield,
  Download,
  FileText,
  ExternalLink,
  Crown,
  TrendingUp,
  Zap,
  Webhook,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../utils/api";
import Swal from "sweetalert2";

const UserDetailsModal = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [platformFeeEnabled, setPlatformFeeEnabled] = useState(user?.platformFee?.enabled || false);
  const [platformFeePercentage, setPlatformFeePercentage] = useState(user?.platformFee?.percentage || 0);
  const [isUpdatingFee, setIsUpdatingFee] = useState(false);
  const [webhookSyncEnabled, setWebhookSyncEnabled] = useState(user?.webhookSync?.enabled || false);
  const [isUpdatingWebhook, setIsUpdatingWebhook] = useState(false);
  const token = localStorage.getItem("token");

  const currentSubscription =
    subscriptions.length > 0
      ? subscriptions.find((s) => s.is_active) || subscriptions[0]
      : {
          _id: null,
          is_active: false,
          status: "inactive",
          plan_id: null,
        };

  useEffect(() => {
    fetchDetails();
  }, [user]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const [subsRes, payRes] = await Promise.all([
        api.get(`admin/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get(`admin/payments`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setSubscriptions(
        subsRes.data.data.filter((s) => s.user_id?._id === user._id),
      );

      setPayments(payRes.data.data.filter((p) => p.user_id?._id === user._id));
    } catch (err) {
      const message = err.response?.data?.message || "Failed to load user details";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      setIsChangingPassword(true);
      await api.post(
        `superadmin/change-password/${user._id}`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully!");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleGenerateLink = async () => {
    try {
      setIsGeneratingLink(true);
      const res = await api.post(
        `superadmin/generate-reset-link/${user._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetLink(res.data.resetLink || res.data.resetUrl);
      toast.success("Reset link generated! Valid for 15 minutes.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate link");
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleUpdatePlatformFee = async () => {
    try {
      setIsUpdatingFee(true);
      await api.put(
        `superadmin/platform-fee/${user._id}`,
        { enabled: platformFeeEnabled, percentage: platformFeePercentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Platform fee updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update platform fee");
    } finally {
      setIsUpdatingFee(false);
    }
  };

  const handleUpdateWebhookSync = async () => {
    try {
      setIsUpdatingWebhook(true);
      await api.put(
        `superadmin/webhook-sync/${user._id}`,
        { enabled: webhookSyncEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Webhook sync updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update webhook sync");
    } finally {
      setIsUpdatingWebhook(false);
    }
  };

  const verifyPayment = async (paymentId) => {
    try {
      await api.patch(
        `payments/${paymentId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // await api.post(
      //   `subscriptions`,
      //   { payment_id: paymentId },
      //   { headers: { Authorization: `Bearer ${token}` } },
      // );

      fetchDetails();
      toast.success("✅ Payment verified & subscription activated");
    } catch (err) {
      const message = err.response?.data?.message || "Verification failed";
      toast.error(message);
    }
  };

  const toggleSubscription = async (subscription) => {
  const isActive = subscription?.is_active;
  const subscriptionId = subscription?._id;

  if (isActive && subscriptionId) {
    const result = await Swal.fire({
      title: "Deactivate Subscription?",
      text: "This will cancel the active subscription.",
      icon: "warning",
      input: "textarea",
      inputPlaceholder: "Enter remark (optional)",
      showCancelButton: true,
      confirmButtonText: "Yes, Deactivate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await api.patch(
        `subscriptions/${subscriptionId}/toggle`,
        {
          status: "inactive",
          remark: result.value || "",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchDetails();
      toast.success("Subscription deactivated successfully");
    } catch (error) {
      const message = error.response?.data?.message || "Failed to deactivate subscription";
      toast.error(message);
    }

    return;
  }

  const result = await Swal.fire({
    title: "Activate Subscription",
    html: `
      <input id="swal-days" type="number" min="1" value="30"
        class="swal2-input" placeholder="Duration (Days)">
      <textarea id="swal-remark"
        class="swal2-textarea"
        placeholder="Remark (optional)"></textarea>
    `,
    showCancelButton: true,
    confirmButtonText: "Activate",
    confirmButtonColor: "#16a34a",
    focusConfirm: false,
    preConfirm: () => {
      const days = document.getElementById("swal-days").value;
      const remark = document.getElementById("swal-remark").value;

      if (!days || Number(days) <= 0) {
        Swal.showValidationMessage("Please enter valid days");
        return false;
      }

      return { days, remark };
    },
  });

  if (!result.isConfirmed) return;

  const { days, remark } = result.value;

  try {
    if (subscriptionId) {
      await api.patch(
        `subscriptions/${subscriptionId}/toggle`,
        {
          status: "active",
          days,
          remark,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      await api.post(
        `subscriptions/admin-create`,
        {
          user_id: user._id,
          days,
          remark,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

    fetchDetails();
    toast.success(`Activated for ${days} days`);
  } catch (error) {
    const message = error.response?.data?.message || "Activation failed";
    toast.error(message);
  }
};

  // One-click grant of permanent free access — no charge, no payment record.
  // Uses the same admin-only plan as the days-based Activate flow above,
  // just with a very long duration so it never needs renewing.
  const grantFreeService = async () => {
    const result = await Swal.fire({
      title: "Grant Free Service?",
      html: `This restaurant will get <strong>permanent free access</strong> (no charge, no expiry for ~100 years). Use this for partner accounts, demo restaurants, etc.`,
      icon: "question",
      input: "textarea",
      inputPlaceholder: "Reason (optional) — e.g. partner account, demo restaurant",
      showCancelButton: true,
      confirmButtonText: "Grant Free Service",
      confirmButtonColor: "#16a34a",
    });

    if (!result.isConfirmed) return;

    try {
      await api.post(
        `subscriptions/admin-create`,
        {
          user_id: user._id,
          days: 36500,
          remark: result.value?.trim() || "Free service granted by admin",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchDetails();
      toast.success("Free service granted — this restaurant now has permanent free access");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to grant free service");
    }
  };


  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const daysLeft = (date) => {
    if (!date) return 0;
    const diff = Math.ceil((new Date(date) - new Date()) / 86400000);
    return diff > 0 ? diff : 0;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "completed":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "failed":
        return "bg-red-100 text-red-800 border-red-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const exportUserData = () => {
    const data = {
      userInfo: user,
      subscriptions: subscriptions,
      payments: payments,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `user-${user._id}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.info("User data exported");
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl overflow-hidden animate-slideUp">
        {/* HEADER */}
        <div className="p-6 border-b bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-3 rounded-xl shadow-md">
                <User className="h-8 w-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {user.owner}
                  </h2>
                  {currentSubscription.is_active && (
                    <span className="px-3 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                      <Crown className="h-3 w-3" />
                      Active Subscriber
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-gray-600 mt-2">
                  <span className="flex items-center gap-2 bg-white px-3 py-1 rounded-lg border">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </span>
                  <span className="flex items-center gap-2 bg-white px-3 py-1 rounded-lg border">
                    <Phone className="h-4 w-4" />
                    {user.mobile || "No phone"}
                  </span>
                  <span className="flex items-center gap-2 bg-white px-3 py-1 rounded-lg border">
                    <Building className="h-4 w-4" />
                    {user.shop_type || "No shop"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportUserData}
                className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-lg flex items-center gap-2 hover:from-blue-100 hover:to-blue-200 transition-all border border-blue-200"
                title="Export User Data"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="border-b bg-white">
          <div className="flex px-6">
            {[
              {
                id: "subscriptions",
                label: "Subscriptions",
                icon: CreditCard,
                count: subscriptions.length,
              },
              {
                id: "payments",
                label: "Payments",
                icon: History,
                count: payments.length,
              },
              { id: "details", label: "User Details", icon: User },
              { id: "platformFee", label: "Platform Fee", icon: IndianRupee },
              { id: "webhookSync", label: "Webhook Sync", icon: Webhook },
              { id: "security", label: "Security", icon: Shield },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-3 px-6 py-4 border-b-2 transition-all ${
                  activeTab === t.id
                    ? "border-blue-600 text-blue-600 bg-blue-50/50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <t.icon className="h-5 w-5" />
                <span className="font-medium">{t.label}</span>
                {t.count !== undefined && (
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full ${
                      activeTab === t.id
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* BODY */}
        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading user details...</p>
            </div>
          ) : (
            <>
              {/* SUBSCRIPTIONS TAB */}
              {activeTab === "subscriptions" && (
                <div className="space-y-6">
                  {/* CURRENT STATUS CARD */}
                  <div className="bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <Package className="h-6 w-6 text-blue-600" />
                          <h3 className="text-xl font-bold text-gray-800">
                            Current Subscription
                          </h3>
                        </div>
                        <p className="text-gray-600">
                          {currentSubscription.plan_id?.title ||
                            "No active plan"}
                          {currentSubscription.is_trial && " (Trial)"}
                        </p>
                        <div className="flex items-center gap-4 mt-3">
                          <span
                            className={`px-4 py-1.5 rounded-full text-sm font-medium ${getStatusColor(currentSubscription.status)}`}
                          >
                            {currentSubscription.status.toUpperCase()}
                          </span>
                          {currentSubscription.expires_on && (
                            <span className="flex items-center gap-2 text-gray-600">
                              <Calendar className="h-4 w-4" />
                              Expires:{" "}
                              {formatDate(currentSubscription.expires_on)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-stretch">
                        <button
                          onClick={() => toggleSubscription(currentSubscription)}
                          className={`px-6 py-3 rounded-xl font-medium transition-all shadow-md ${
                            currentSubscription.is_active
                              ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                              : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                          }`}
                        >
                          {currentSubscription.is_active
                            ? "Deactivate"
                            : "Activate Now"}
                        </button>
                        {!currentSubscription.is_active && (
                          <button
                            onClick={grantFreeService}
                            className="px-6 py-2 rounded-xl font-medium text-sm transition-all border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                            title="Grant permanent free access — no charge"
                          >
                            🎁 Grant Free Service
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* SUBSCRIPTIONS LIST */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Subscription History
                      </h3>
                      <span className="text-sm text-gray-500">
                        {subscriptions.length} subscription
                        {subscriptions.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {subscriptions.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-2xl border">
                        <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No subscriptions found</p>
                        <p className="text-sm text-gray-500 mt-1">
                          This user has no subscription history
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {subscriptions.map((s) => (
                          <div
                            key={s._id}
                            className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="font-semibold text-gray-800 text-lg">
                                  {s.plan_id?.title || "Custom Plan"}
                                </h4>
                                {s.is_trial && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full mt-1">
                                    <Zap className="h-3 w-3" />
                                    Trial Period
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(s.status)}`}
                                >
                                  {s.status.toUpperCase()}
                                </span>
                                {s.is_active && (
                                  <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <p className="text-sm text-gray-500 mb-1">
                                  Start Date
                                </p>
                                <p className="font-medium">
                                  {formatDate(s.current_period_start)}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500 mb-1">
                                  End Date
                                </p>
                                <p className="font-medium">
                                  {formatDate(s.expires_on)}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500 mb-1">
                                  Days Remaining
                                </p>
                                <p
                                  className={`font-bold ${daysLeft(s.expires_on) <= 7 ? "text-red-600" : "text-emerald-600"}`}
                                >
                                  {daysLeft(s.expires_on)}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500 mb-1">
                                  Auto Renew
                                </p>
                                <p className="font-medium">
                                  {s.auto_renew ? "Yes" : "No"}
                                </p>
                              </div>
                            </div>

                            <div className="pt-4 border-t">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">
                                  Created: {formatDate(s.createdAt)}
                                </span>
                                <button
                                  onClick={() => toggleSubscription(s)}
                                  className={`px-4 py-1.5 text-sm rounded-lg ${
                                    s.is_active
                                      ? "bg-red-50 text-red-600 hover:bg-red-100"
                                      : "bg-green-50 text-green-600 hover:bg-green-100"
                                  }`}
                                >
                                  {s.is_active ? "Deactivate" : "Activate"}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PAYMENTS TAB */}
              {activeTab === "payments" && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        Payment History
                      </h3>
                      <p className="text-sm text-gray-500">
                        Total {payments.length} payment
                        {payments.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-lg font-bold text-gray-800">
                      <IndianRupee className="h-5 w-5" />
                      {payments
                        .reduce((sum, p) => sum + (p.total_amount || 0), 0)
                        .toLocaleString()}
                    </div>
                  </div>

                  {payments.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-2xl border">
                      <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No payment history</p>
                      <p className="text-sm text-gray-500 mt-1">
                        This user hasn't made any payments yet
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-xl border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Date & Transaction
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Amount
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {payments.map((p) => {
                            const isVerified = p.is_verified;

                            return (
                              <tr
                                key={p._id}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-6 py-4">
                                  <div className="font-medium">
                                    {formatDate(p.createdAt)}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {p.transaction_id
                                      ? `TXN: ${p.transaction_id}`
                                      : "No TXN ID"}
                                  </div>
                                  {p.payment_method && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Method: {p.payment_method}
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-1 font-bold text-gray-800">
                                    <IndianRupee className="h-4 w-4" />
                                    {p.total_amount?.toLocaleString()}
                                  </div>
                                  {p.amount && p.amount !== p.total_amount && (
                                    <div className="text-sm text-gray-500 line-through">
                                      ₹{p.amount?.toLocaleString()}
                                    </div>
                                  )}
                                  {p.coupon_code && (
                                    <div className="text-xs text-emerald-600 mt-1">
                                      Coupon: {p.coupon_code}
                                    </div>
                                  )}
                                  {p.discount_amount > 0 && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      -₹{p.discount_amount} discount
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    {p.payment_status === "completed" ? (
                                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    ) : (
                                      <Clock className="h-4 w-4 text-amber-500" />
                                    )}
                                    <span
                                      className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(p.payment_status)}`}
                                    >
                                      {p.payment_status.toUpperCase()}
                                    </span>
                                  </div>
                                  {isVerified && (
                                    <div className="mt-2 flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs w-fit">
                                      <CheckCircle className="h-3 w-3" />
                                      Verified
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-col gap-2">
                                    <button
                                      disabled={p.is_verified}
                                      onClick={() => verifyPayment(p.id)}
                                      className={`px-4 py-2 rounded-lg flex items-center gap-2 w-fit ${
                                        p.is_verified
                                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                          : "bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600"
                                      }`}
                                    >
                                      {p.is_verified
                                        ? "Verified"
                                        : "Verify Payment"}
                                    </button>

                                    {p.receipt_url && (
                                      <a
                                        href={p.receipt_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3 py-1.5 text-blue-600 hover:text-blue-800 text-sm w-fit"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        View Receipt
                                      </a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* USER DETAILS TAB */}
              {activeTab === "details" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* USER INFO CARD */}
                  <div className="bg-gradient-to-br from-blue-50 to-teal-50 border border-blue-200 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <User className="h-5 w-5" />
                      User Information
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">User ID</span>
                        <code className="bg-white px-3 py-1 rounded-lg border text-sm font-mono">
                          {user._id.slice(-8)}
                        </code>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Account Type</span>
                        <span className="font-medium">
                          {user.type || "Standard"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Shop Type</span>
                        <span className="font-medium">
                          {user.shop_type || "Not specified"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Joined Date</span>
                        <span className="font-medium">
                          {formatDate(user.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* STATISTICS CARD */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      User Statistics
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl border text-center">
                        <div className="text-2xl font-bold text-gray-800 mb-1">
                          {subscriptions.length}
                        </div>
                        <div className="text-sm text-gray-600">
                          Subscriptions
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-xl border text-center">
                        <div className="text-2xl font-bold text-gray-800 mb-1">
                          {payments.length}
                        </div>
                        <div className="text-sm text-gray-600">Payments</div>
                      </div>
                      <div className="bg-white p-4 rounded-xl border text-center">
                        <div className="text-2xl font-bold text-gray-800 mb-1">
                          {currentSubscription.is_active
                            ? "Active"
                            : "Inactive"}
                        </div>
                        <div className="text-sm text-gray-600">Status</div>
                      </div>
                      <div className="bg-white p-4 rounded-xl border text-center">
                        <div className="text-2xl font-bold text-gray-800 mb-1">
                          {currentSubscription.is_trial ? "Yes" : "No"}
                        </div>
                        <div className="text-sm text-gray-600">On Trial</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* PLATFORM FEE TAB */}
              {activeTab === "platformFee" && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 border-b border-orange-100">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                        <IndianRupee className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">Platform Fee Configuration</h3>
                        <p className="text-gray-600 mt-1">
                          Configure a customer-funded commission to be charged on this restaurant's orders.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <div>
                        <h4 className="font-bold text-gray-800">Enable Platform Fee</h4>
                        <p className="text-sm text-gray-500">
                          Turn on to automatically calculate and apply this fee at customer checkout.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={platformFeeEnabled}
                          onChange={(e) => setPlatformFeeEnabled(e.target.checked)}
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500"></div>
                      </label>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-gray-700">
                        Fee Percentage (%)
                      </label>
                      <div className="relative max-w-xs">
                        <input
                          type="number"
                          value={platformFeePercentage}
                          onChange={(e) => setPlatformFeePercentage(e.target.value)}
                          disabled={!platformFeeEnabled}
                          min="0"
                          max="100"
                          step="0.1"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="e.g. 5"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500 font-bold">
                          %
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        This percentage is calculated on the post-tax order total.
                      </p>
                    </div>

                    <div className="pt-4 border-t">
                      <button
                        onClick={handleUpdatePlatformFee}
                        disabled={isUpdatingFee}
                        className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-md transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isUpdatingFee ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Saving...
                          </>
                        ) : (
                          "Save Settings"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* WEBHOOK SYNC TAB */}
              {activeTab === "webhookSync" && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 border-b border-teal-100">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-teal-100 text-teal-600 rounded-xl">
                        <Webhook className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">Webhook Sync Configuration</h3>
                        <p className="text-gray-600 mt-1">
                          Enable to allow this restaurant to receive automatic orders via Swiggy / Zomato webhooks.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <div>
                        <h4 className="font-bold text-gray-800">Enable Webhook Sync</h4>
                        <p className="text-sm text-gray-500">
                          When enabled, the restaurant will see webhook URLs in their Online Orders panel.
                          Disable to hide the webhook setup entirely.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={webhookSyncEnabled}
                          onChange={(e) => setWebhookSyncEnabled(e.target.checked)}
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-teal-500"></div>
                      </label>
                    </div>

                    <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3">
                      <p className="text-sm font-semibold text-amber-800 mb-1">Note</p>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        Swiggy and Zomato require official tech-partner approval before they accept webhook URLs.
                        Only enable this for restaurants that have completed that onboarding process.
                      </p>
                    </div>

                    <div className="pt-4 border-t">
                      <button
                        onClick={handleUpdateWebhookSync}
                        disabled={isUpdatingWebhook}
                        className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-md transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isUpdatingWebhook ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Saving...
                          </>
                        ) : (
                          "Save Settings"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* SECURITY TAB */}
              {activeTab === "security" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* DIRECT PASSWORD CHANGE */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <Shield className="h-6 w-6 text-orange-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">Change Password</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Directly set a new password for this restaurant account. They will be able to log in with this immediately.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter at least 6 characters"
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                        />
                      </div>
                      <button
                        onClick={handlePasswordChange}
                        disabled={isChangingPassword || !newPassword || newPassword.length < 6}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isChangingPassword ? "Saving..." : "Update Password"}
                      </button>
                    </div>
                  </div>

                  {/* GENERATE RESET LINK */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <ExternalLink className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">Generate Access Link</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Generate a secure, one-time link valid for 15 minutes. The restaurant owner can use this link to securely reset their own password.
                    </p>
                    
                    <button
                      onClick={handleGenerateLink}
                      disabled={isGeneratingLink}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors mb-4"
                    >
                      {isGeneratingLink ? "Generating..." : "Generate 15-Min Link"}
                    </button>

                    {resetLink && (
                      <div className="mt-4 p-4 bg-gray-50 border rounded-lg">
                        <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">Temporary Link:</p>
                        <div className="flex items-center gap-2">
                          <input 
                            type="text" 
                            readOnly 
                            value={resetLink} 
                            className="w-full text-sm p-2 bg-white border rounded text-gray-700 focus:outline-none"
                            onClick={(e) => e.target.select()}
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(resetLink);
                              toast.success("Link copied to clipboard!");
                            }}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Last updated: {new Date().toLocaleDateString()}
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportUserData}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Data
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add CSS animations
const style = document.createElement("style");
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
  
  .animate-slideUp {
    animation: slideUp 0.4s ease-out;
  }
`;
document.head.appendChild(style);

export default UserDetailsModal;
